import { cn } from "../../lib/utils"

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {}

function Skeleton({ className, ...props }: SkeletonProps) {
  return (
    <div
      className={cn("skeleton", className)}
      {...props}
    />
  )
}

// Pre-built skeleton patterns for common layouts

function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn("bg-white rounded-lg p-6 shadow-sm", className)}>
      <div className="flex items-center gap-4">
        <Skeleton className="w-12 h-12 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
    </div>
  )
}

function SkeletonDashboardCards() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-lg p-6 shadow-sm">
          <div className="flex flex-col items-center gap-3">
            <Skeleton className="w-12 h-12 rounded-full" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      ))}
    </div>
  )
}

function SkeletonLeaderboardRow() {
  return (
    <div className="flex items-center gap-3 p-4 border-b border-gray-100">
      <Skeleton className="w-8 h-8 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-20" />
      </div>
      <div className="flex items-center gap-4">
        <Skeleton className="h-6 w-8" />
        <Skeleton className="h-6 w-10" />
      </div>
    </div>
  )
}

function SkeletonLeaderboard() {
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {[...Array(8)].map((_, i) => (
        <SkeletonLeaderboardRow key={i} />
      ))}
    </div>
  )
}

function SkeletonTeamGrid() {
  return (
    <div className="grid grid-cols-2 gap-2">
      {[...Array(16)].map((_, i) => (
        <div key={i} className="p-2.5 rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center gap-2">
            <Skeleton className="w-5 h-5 rounded-full" />
            <Skeleton className="h-4 flex-1" />
          </div>
        </div>
      ))}
    </div>
  )
}

function SkeletonEntryList() {
  return (
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-16" />
          </div>
          <div className="text-right space-y-2">
            <Skeleton className="h-6 w-12 ml-auto" />
            <Skeleton className="h-3 w-10 ml-auto" />
          </div>
        </div>
      ))}
    </div>
  )
}

export { 
  Skeleton, 
  SkeletonCard, 
  SkeletonDashboardCards, 
  SkeletonLeaderboard,
  SkeletonLeaderboardRow,
  SkeletonTeamGrid,
  SkeletonEntryList
}

